import React, { useEffect, useRef, useState } from 'react';
import { NumberFormatValues } from 'react-number-format';
import { ClipLoader } from 'react-spinners';

import { Button, ModalExitIcon } from 'components/core';
import { BankIcon } from 'assets/bank-icon';
import { EnrollmentBankAccount } from 'src/state/dutchiePay-enrollment';

import {
  AmountCurrencySymbol,
  AmountDisplay,
  AmountErrorText,
  AmountPresetButton,
  AmountPresetRow,
  AmountValue,
  BankAccountDetail,
  BankInfoGroup,
  BankLogo,
  BankLogoTile,
  BankName,
  BankRow,
  BankTextGroup,
  CtaLoaderWrap,
  CustomAmountInput,
  ModalTitle,
  SectionLabel,
  SubmittingHelpText,
} from './add-funds-modal.styles';
import { MIN_LOAD_CENTS, formatAmountTrimmed, parseAmountToCents, validateLoadAmount } from './add-funds-utils';

const PRESET_AMOUNTS_CENTS = [500, 1000, 2000, 5000] as const;

const AMOUNT_ERROR_ID = 'add-funds-amount-error';

type AmountSelection = number | 'other';

export type AddFundsModalProps = {
  bankAccount: EnrollmentBankAccount;
  isSubmitting: boolean;
  onClose: () => void;
  onChangeBank: () => void;
  onSubmit: (amountCents: number) => void;
  loadedTodayCents?: number;
  minAmountCents?: number;
};

export const AddFundsModalContent = ({
  bankAccount,
  isSubmitting,
  onClose,
  onChangeBank,
  onSubmit,
  loadedTodayCents = 0,
  minAmountCents = MIN_LOAD_CENTS,
}: AddFundsModalProps): JSX.Element => {
  const [selection, setSelection] = useState<AmountSelection>(PRESET_AMOUNTS_CENTS[0]);
  const [customAmount, setCustomAmount] = useState('');
  const [customAmountDisplay, setCustomAmountDisplay] = useState('');
  const [customAmountFloat, setCustomAmountFloat] = useState<number | null>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selection === 'other') {
      customInputRef.current?.focus();
    }
  }, [selection]);

  const isCustom = selection === 'other';
  let amountCents: number | null = null;
  if (!isCustom) {
    amountCents = selection;
  } else if (customAmountFloat !== null) {
    amountCents = parseAmountToCents(customAmountFloat);
  }

  const amountError = validateLoadAmount({ amountCents, loadedTodayCents, minAmountCents });

  const isSubmittable = amountCents !== null && !amountError;

  const financialInstitutionLogo =
    bankAccount.financialInstitutionLogo && `data:image/png;base64, ${bankAccount.financialInstitutionLogo}`;

  const handleCustomAmountChange = (values: NumberFormatValues): void => {
    setCustomAmount(values.value);
    setCustomAmountDisplay(values.formattedValue);
    setCustomAmountFloat(values.floatValue ?? null);
  };

  const handleSubmit = (): void => {
    if (amountCents === null || amountError || isSubmitting) {
      return;
    }
    onSubmit(amountCents);
  };

  let ctaLabel = 'Enter an amount';
  if (amountCents !== null) {
    ctaLabel = `Add ${formatAmountTrimmed(amountCents)}`;
  }

  return (
    <>
      {!isSubmitting && <ModalExitIcon onClick={onClose} />}

      <ModalTitle size='xxlarge' tag='h2'>
        Add funds
      </ModalTitle>

      <SectionLabel size='small' weight='semi' casing='uppercase' tag='span'>
        From
      </SectionLabel>
      <BankRow>
        <BankInfoGroup>
          <BankLogoTile>
            {financialInstitutionLogo ? (
              <BankLogo src={financialInstitutionLogo} alt='' />
            ) : (
              <BankIcon height={24} width={24} />
            )}
          </BankLogoTile>
          <BankTextGroup>
            <BankName size='medium' weight='semi' tag='span'>
              {bankAccount.bankName}
            </BankName>
            <BankAccountDetail size='small' tag='span'>
              Account ••{bankAccount.lastDigits}
            </BankAccountDetail>
          </BankTextGroup>
        </BankInfoGroup>
        <Button type='button' variant='outlinePrimary' size='small' onClick={onChangeBank}>
          Change
        </Button>
      </BankRow>

      <SectionLabel size='xxsmall' weight='semi' casing='uppercase' tag='span'>
        Amount
      </SectionLabel>
      <AmountPresetRow role='group' aria-label='Amount'>
        {PRESET_AMOUNTS_CENTS.map((presetCents) => (
          <AmountPresetButton
            key={presetCents}
            type='button'
            size='medium'
            isSelected={selection === presetCents}
            aria-pressed={selection === presetCents}
            disabled={isSubmitting}
            onClick={() => setSelection(presetCents)}
          >
            {formatAmountTrimmed(presetCents)}
          </AmountPresetButton>
        ))}
        <AmountPresetButton
          type='button'
          size='medium'
          isSelected={selection === 'other'}
          aria-pressed={selection === 'other'}
          disabled={isSubmitting}
          onClick={() => setSelection('other')}
        >
          Other
        </AmountPresetButton>
      </AmountPresetRow>

      <AmountDisplay hasError={!!amountError}>
        <AmountCurrencySymbol size='xlarge' tag='span' aria-hidden>
          $
        </AmountCurrencySymbol>
        {isCustom ? (
          <CustomAmountInput
            getInputRef={customInputRef}
            value={customAmount}
            onValueChange={handleCustomAmountChange}
            thousandSeparator
            decimalScale={2}
            allowNegative={false}
            inputMode='decimal'
            autoComplete='off'
            disabled={isSubmitting}
            size={Math.min(Math.max(customAmountDisplay.length, 1), 12)}
            aria-label='Custom amount'
            aria-invalid={!!amountError}
            aria-describedby={amountError ? AMOUNT_ERROR_ID : undefined}
          />
        ) : (
          <AmountValue size='xlarge' tag='span'>
            {selection / 100}
          </AmountValue>
        )}
      </AmountDisplay>
      {amountError ? (
        <AmountErrorText size='small' tag='p' id={AMOUNT_ERROR_ID} role='alert'>
          {amountError}
        </AmountErrorText>
      ) : null}

      {isSubmitting ? (
        <SubmittingHelpText size='small' tag='p'>
          Holding your spot — we ping your bank to make sure the funds are there.
        </SubmittingHelpText>
      ) : null}

      <Button
        type='button'
        variant='primary'
        size='medium'
        fluid
        disabled={!isSubmittable || isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? (
          <>
            <CtaLoaderWrap>
              <ClipLoader color='var(--dcm-add-funds-modal-spinner-color, #fff)' size={14} />
            </CtaLoaderWrap>
            Confirming with your bank...
          </>
        ) : (
          ctaLabel
        )}
      </Button>
    </>
  );
};
